import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { BarChart3, TrendingUp, DollarSign, FileText, Download, PieChart, ShoppingBag } from 'lucide-react';

const Reports = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState({ start: '', end: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('income');

  const fetchReport = async (type) => {
    if (!period.start || !period.end) return;
    setLoading(true);
    setActiveTab(type);
    try {
      const endpoints = {
        income: '/reports/income-statement',
        cashflow: '/reports/cashflow',
        sales: '/reports/sales-summary',
        purchases: '/reports/purchases-summary',
        balance: '/reports/balance-sheet',
      };
      const endpoint = endpoints[type] || '/reports/balance-sheet';
      const params = type === 'balance' ? {} : { start: period.start, end: period.end };
      const res = await api.get(endpoint, { params });
      setReport(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const downloadPdf = async () => {
    if ((activeTab !== 'balance') && (!period.start || !period.end)) {
      alert('Please select start and end dates');
      return;
    }
    try {
      const pdfEndpoints = {
        income: '/reports/pdf/income-statement',
        cashflow: '/reports/pdf/cashflow',
        balance: '/reports/pdf/balance-sheet',
        sales: '/reports/pdf/sales-summary',
        purchases: '/reports/pdf/purchases-summary',
      };
      const endpoint = pdfEndpoints[activeTab] || '/reports/pdf/balance-sheet';
      const params = activeTab === 'balance' ? {} : { start: period.start, end: period.end };
      const response = await api.get(endpoint, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeTab}-report-${period.start}-to-${period.end}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); alert('Failed to download PDF'); }
  };

  const formatCurrency = (v) => {
    if (v === null || v === undefined || v === '') return 'TZS 0';
    const num = typeof v === 'object' ? 0 : Number(v);
    return 'TZS ' + num.toLocaleString('en-TZ');
  };

  const renderValue = (key, value) => {
    if (value === null || value === undefined) return <span className="text-gray-500">-</span>;

    if (typeof value === 'object' && !Array.isArray(value)) {
      if (value.hasOwnProperty('count') || value.hasOwnProperty('revenue') || value.hasOwnProperty('amount')) {
        return (
          <div className="text-right">
            {value.count !== undefined && <div className="text-xs text-gray-400">{value.count} records</div>}
            {(value.revenue !== undefined || value.amount !== undefined) && (
              <div className="text-emerald-400 font-medium">{formatCurrency(value.revenue || value.amount)}</div>
            )}
            {value.collected !== undefined && <div className="text-xs text-blue-400">Collected: {formatCurrency(value.collected)}</div>}
            {value.paid !== undefined && <div className="text-xs text-blue-400">Paid: {formatCurrency(value.paid)}</div>}
            {value.pending !== undefined && Number(value.pending) > 0 && (
              <div className="text-xs text-yellow-400">Pending: {formatCurrency(value.pending)}</div>
            )}
            {value.estimatedProfit !== undefined && (
              <div className="text-xs text-emerald-300">Est. Profit: {formatCurrency(value.estimatedProfit)}</div>
            )}
          </div>
        );
      }

      if (key === 'paymentStatusSummary') {
        return (
          <div className="text-right space-y-1">
            {Object.entries(value).map(([k, v]) => (
              <div key={k} className="flex items-center justify-end gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${
                  k === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : k === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  k === 'partial' ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'
                }`}>{k}</span>
                <span className="text-white text-xs">{String(v)}</span>
              </div>
            ))}
          </div>
        );
      }

      if (key === 'revenueByType' || key === 'expensesByCategory' || key === 'purchasesByType' || key === 'supplierSummary') {
        return (
          <div className="text-right space-y-1">
            {Object.entries(value).map(([k, v]) => (
              <div key={k} className="text-xs">
                <span className="text-gray-400 capitalize">{k.replace(/_/g, ' ')}: </span>
                <span className="text-gray-300">{typeof v === 'number' ? formatCurrency(v) : typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
              </div>
            ))}
          </div>
        );
      }

      return (
        <div className="text-right space-y-2">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="border-b border-white/5 pb-2 last:border-0 last:pb-0">
              <div className="text-xs font-medium text-gray-300 capitalize mb-1">{k.replace(/_/g, ' ')}</div>
              {typeof v === 'object' && renderValue(k, v)}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === 'number') return <span>{formatCurrency(value)}</span>;
    return <span>{String(value)}</span>;
  };

  const formatLabel = (key) => {
    return key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
      .replace(/avg /i, 'Avg. ').replace(/by /i, 'by ').replace(/of /i, 'of ');
  };

  const tabs = [
    { key: 'income', label: t('income_statement'), icon: TrendingUp },
    { key: 'balance', label: t('balance_sheet'), icon: DollarSign },
    { key: 'cashflow', label: t('cashflow'), icon: BarChart3 },
    { key: 'sales', label: 'Sales Summary', icon: PieChart },
    { key: 'purchases', label: 'Purchases Summary', icon: ShoppingBag },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-white">{t('reports')}</h2>
        <p className="text-gray-400 text-sm mt-1">Financial reports & analysis</p>
      </div>

      <div className="bg-[#0d1410] border border-white/5 rounded-xl p-4">
        <p className="text-gray-400 text-xs mb-3">Select period</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1"><label className="block text-xs text-gray-500 mb-1.5">Start Date</label>
            <input type="date" value={period.start} onChange={e => setPeriod({...period, start: e.target.value})}
              className="w-full px-4 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
          </div>
          <div className="flex-1"><label className="block text-xs text-gray-500 mb-1.5">End Date</label>
            <input type="date" value={period.end} onChange={e => setPeriod({...period, end: e.target.value})}
              className="w-full px-4 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
          </div>
        </div>
      </div>

      <div className="flex gap-2 bg-[#0d1410] border border-white/5 rounded-xl p-1 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => fetchReport(tab.key)}
            className={`flex items-center gap-1.5 px-3 md:px-4 py-2.5 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'
            }`}><tab.icon size={15} /> {tab.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 text-sm">Generating report...</p></div></div>
      ) : report ? (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div><h3 className="text-white font-bold text-lg">{report.shopName}</h3>
              <p className="text-gray-500 text-sm">{report.periodStart && report.periodEnd ? `${report.periodStart} to ${report.periodEnd}` : 'Current'}</p>
            </div>
            {report.status && (
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                report.status?.includes('Profit') || report.status === 'POSITIVE' ? 'bg-emerald-500/20 text-emerald-400' :
                report.status?.includes('Loss') || report.status === 'NEGATIVE' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'
              }`}>{report.status}</span>
            )}
          </div>

          <div className="space-y-1 mb-6">
            {Object.entries(report).map(([key, value]) => {
              if (['shopName', 'periodStart', 'periodEnd', 'status', 'generatedAt'].includes(key)) return null;
              const formattedKey = formatLabel(key);

              if (key === 'salesByType' || key === 'revenueByType' || key === 'expensesByCategory' || key === 'purchasesByType' || key === 'supplierSummary') {
                return (
                  <React.Fragment key={key}>
                    <div className="pt-4 pb-2 mt-2 border-t border-white/5">
                      <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">{formattedKey}</span>
                    </div>
                    <div className="pl-2">{renderValue(key, value)}</div>
                  </React.Fragment>
                );
              }

              if (key === 'paymentStatusSummary') {
                return (
                  <React.Fragment key={key}>
                    <div className="pt-4 pb-2 mt-2 border-t border-white/5">
                      <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">{formattedKey}</span>
                    </div>
                    <div className="pl-2">{renderValue(key, value)}</div>
                  </React.Fragment>
                );
              }

              return (
                <div key={key} className="flex justify-between items-center p-3 rounded-lg hover:bg-white/[0.02] transition">
                  <span className="text-gray-400 text-sm pr-4">{formattedKey}</span>
                  <span className="text-white font-medium text-sm text-right">{renderValue(key, value)}</span>
                </div>
              );
            })}
          </div>

          <button onClick={downloadPdf}
            className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500/10 text-emerald-400 rounded-xl font-medium hover:bg-emerald-500/20 transition text-sm border border-emerald-500/20">
            <Download size={18} /> Download PDF Report</button>
        </div>
      ) : (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-12 text-center">
          <FileText size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 font-medium">Select dates and a report type</p>
          <p className="text-gray-600 text-sm mt-1">Generate financial reports to analyze your business.</p>
        </div>
      )}
    </div>
  );
};

export default Reports;